import React, { useState, useEffect } from "react";
import { ComparisonFilters } from "./ComparisonFilters";
import { RadarComparison } from "./RadarComparison";
import { HeatmapComparison } from "./HeatmapComparison";
import { GroupedBarComparison } from "./GroupedBarComparison";

interface SocieteRanking {
  societe_id: string;
  societe_nom: string;
  nb_candidatures: number;
  score_moyen: number;
  decision_counts: {
    shortlist: number;
    consider: number;
    reject: number;
  };
  candidats: Array<{
    id: string;
    nom: string;
    score: number;
    decision: string;
    created_at: string;
  }>;
  scores_criteres: { [key: string]: { score: number; count: number } };
}

interface ComparisonViewProps {
  rankings: SocieteRanking[];
}

export const ComparisonView: React.FC<ComparisonViewProps> = ({ rankings }) => {
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const [selectedCriteria, setSelectedCriteria] = useState<string[]>(['overall_score']);
  const [visualizationType, setVisualizationType] = useState<string>('heatmap');
  const [filterMode, setFilterMode] = useState<'top' | 'manual' | 'decision'>('top');
  const [topCount, setTopCount] = useState<number>(5);
  const [selectedDecisions, setSelectedDecisions] = useState<string[]>(['shortlist', 'consider']);

  // Auto-select companies based on filter mode
  useEffect(() => {
    if (rankings.length === 0) return;

    if (filterMode === 'top') {
      const topCompanies = rankings.slice(0, topCount).map(r => r.societe_id);
      setSelectedCompanies(topCompanies);
    } else if (filterMode === 'decision') {
      const filteredCompanies = rankings.filter(ranking => {
        return selectedDecisions.some(decision => {
          if (decision === 'shortlist') return ranking.decision_counts.shortlist > 0;
          if (decision === 'consider') return ranking.decision_counts.consider > 0;
          if (decision === 'reject') return ranking.decision_counts.reject > 0;
          return false;
        });
      }).map(r => r.societe_id);
      setSelectedCompanies(filteredCompanies);
    }
    // For manual mode, keep existing selection
  }, [rankings, filterMode, topCount, selectedDecisions]);

  // Auto-select all criteria by default
  useEffect(() => {
    if (rankings.length > 0) {
      // Get all unique criteria from all rankings
      const allCriteria = Array.from(
        new Set(rankings.flatMap(r => Object.keys(r.scores_criteres)))
      );
      
      const allCriteriaKeys = [
        'overall_score',
        ...allCriteria.map(c => `criteria_${c}`),
      ];
      
      setSelectedCriteria(allCriteriaKeys);
    }
  }, [rankings]);

  const renderVisualization = () => {
    switch (visualizationType) {
      case 'radar':
        return (
          <RadarComparison
            rankings={rankings}
            selectedCompanies={selectedCompanies}
            selectedCriteria={selectedCriteria}
          />
        );
      case 'heatmap':
        return (
          <HeatmapComparison
            rankings={rankings}
            selectedCompanies={selectedCompanies}
            selectedCriteria={selectedCriteria}
          />
        );
      case 'bar':
        return (
          <GroupedBarComparison
            rankings={rankings}
            selectedCompanies={selectedCompanies}
            selectedCriteria={selectedCriteria}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <ComparisonFilters
        rankings={rankings}
        selectedCompanies={selectedCompanies}
        selectedCriteria={selectedCriteria}
        visualizationType={visualizationType}
        filterMode={filterMode}
        topCount={topCount}
        selectedDecisions={selectedDecisions}
        onCompaniesChange={setSelectedCompanies}
        onCriteriaChange={setSelectedCriteria}
        onVisualizationTypeChange={setVisualizationType}
        onFilterModeChange={setFilterMode}
        onTopCountChange={setTopCount}
        onDecisionsChange={setSelectedDecisions}
      />
      
      {renderVisualization()}
    </div>
  );
};
